import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError, SupabaseClient } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { BackendSuccessResponse } from '@/types/response/BackendSuccessResponse';
import type { RegisterUserRequest } from '$/requests/RegisterUser.ts';

const useRegisterUser = () => {
  const client = supabaseClient;

  const mutationFn = async (request: RegisterUserRequest) => {
    return registerUser(client, request);
  };

  return useMutation({
    mutationFn,
  });
};

export default useRegisterUser;

async function registerUser(
  client: SupabaseClient,
  request: RegisterUserRequest
): Promise<BackendSuccessResponse<{ message: string; token: string }>> {
  const { data, error } = await client.functions.invoke('register_user', {
    body: { ...request },
  });

  if (error instanceof FunctionsHttpError) {
    let errorPayload: unknown;

    try {
      errorPayload = await error.context.json();
    } catch {
      throw new Error(error.message || 'Registration failed. Please try again.');
    }

    throw new Error(getFunctionErrorMessage(errorPayload));
  }

  if (error) {
    throw error;
  }

  return data;
}

function getFunctionErrorMessage(payload: unknown): string {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return 'Registration failed. Please try again.';
  }

  const response = payload as Record<string, unknown>;
  const possibleErrors = [response.errors, response.message, response.error];

  for (const possibleError of possibleErrors) {
    const messages = collectErrorMessages(possibleError);

    if (messages.length > 0) {
      return messages.join(', ');
    }
  }

  return 'Registration failed. Please try again.';
}

function collectErrorMessages(value: unknown): string[] {
  if (typeof value === 'string') {
    return value.trim() ? [value] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectErrorMessages);
  }

  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(collectErrorMessages);
  }

  return [];
}